import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import styles from "./Image.module.scss";

const VALID_SIZE_REGEX = /^\d*(\.)*\d+px$/;

/**
 * Image renders a semantic HTML <img> with a simple interface.
 */
export const Image = (props) => {
  const {
    alt,
    className,
    content,
    fetchPriority,
    fitToContainer,
    height,
    onLoad,
    src,
    sources,
    width,
    ...otherImageProps
  } = props;

  return (
    <picture className={styles.container}>
      {sources?.length > 0 && renderSourceSets()}
      <img {...getImageProps()} />
    </picture>
  );

  function renderSourceSets() {
    return sources.map(({ mediaQuery, src: breakpointSrc }) => (
      <source key={`${mediaQuery}-source`} media={mediaQuery} srcSet={breakpointSrc} />
    ));
  }

  function getImageProps() {
    const imageProps = {
      alt,
      className: getClass(),
      fetchpriority: fetchPriority,
      onLoad,
      src: getSrc(),
      width,
      ...otherImageProps,
    };

    if (fitToContainer && sizeIsValid(width) && sizeIsValid(height)) {
      const numericHeight = parseFloat(height);
      const numericWidth = parseFloat(width);

      imageProps.style = {
        aspectRatio: numericWidth / numericHeight,
      };
    } else {
      imageProps.height = height;
    }

    return imageProps;
  }

  function getClass() {
    return classNames({
      [className]: !!className,
      [styles.fitToContainer]: fitToContainer && sizeIsValid(width) && sizeIsValid(height),
    });
  }

  function getSrc() {
    return content ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(content)}` : src;
  }

  function sizeIsValid(size) {
    return parseFloat(size) > 0 && (typeof size !== "string" || VALID_SIZE_REGEX.test(size));
  }
};

Image.displayName = "Image";

export const imageSourcesPropType = PropTypes.arrayOf(
  PropTypes.shape({
    mediaQuery: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
  })
);
export const imageFetchPriorityPropType = PropTypes.oneOf(["auto", "high", "low"]);

Image.propTypes = {
  /** Alternate text for the image should it not appear. */
  alt: PropTypes.string.isRequired,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** The content to use if the image represents an inline SVG component */
  content: PropTypes.string,

  /** The fetch priority to use when loading the image */
  fetchPriority: imageFetchPriorityPropType,

  /**
   * This value determines if the image should be constrained to the width
   * of its container. If true the height value will not be set on the rendered
   * image and the aspect ratio will be used instead. If either the width or
   * height value are not set or are invalid then this value will be ignored.
   */
  fitToContainer: PropTypes.bool,

  /**
   * Height of the image, in pixels. If the fitToContainer prop is true this
   * value will not be added to the rendered image and instead will be used to
   * calculate the aspect ratio.
   */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * An optional event handler that notifies the consumer when the image has loaded.
   */
  onLoad: PropTypes.func,

  /** The list of sources to use when rendering the image */
  sources: imageSourcesPropType,

  /**
   * URL to use to download the image. This value should be defined even if
   * the sources prop is also defined as it will serve as the fallback in the
   * case none of the sources media queries are satisfied.
   */
  src: PropTypes.string.isRequired,

  /** Width of the image, in pixels. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
